/**
 * Tarjeta de alerta de registros pendientes en Producción Diaria.
 *
 * Muestra las combinaciones (Fecha, Naves) que están con atraso
 * (más de `dias_tolerancia` días sin registrar). Permite expandir el
 * detalle y, al click en una fecha+nave, pre-rellena el formulario de
 * arriba para que el usuario solo deba meter los valores.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Calendar, X, Settings2, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { getRegistrosFaltantes } from '../../lib/api';
import { formatDate } from '../../lib/dateUtils';

const LOOKBACK_OPTIONS = [
  { value: 7, label: 'Últimos 7 días' },
  { value: 14, label: 'Últimos 14 días' },
  { value: 30, label: 'Últimos 30 días' },
  { value: 60, label: 'Últimos 60 días' },
  { value: 0, label: 'Todo el historial' },
];

const TOLERANCIA_OPTIONS = [
  { value: 0, label: 'Estricto (ayer ya cuenta)' },
  { value: 1, label: '1 día' },
  { value: 2, label: '2 días' },
  { value: 3, label: '3 días' },
];

/** Color del badge según días de atraso. */
const atrasoVariant = (d) => {
  if (d >= 14) return 'destructive';
  if (d >= 7) return 'default';
  return 'secondary';
};

export const RegistrosFaltantesCard = ({ onSelectFaltante, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [tolerancia, setTolerancia] = useState(2);
  // lookback: 0 = todo el historial; >0 = últimos N días
  const [lookback, setLookback] = useState(0);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const params = { dias_tolerancia: tolerancia };
      if (lookback > 0) params.dias_lookback = lookback;
      const res = await getRegistrosFaltantes(params);
      setData(res.data);
    } catch (err) {
      console.warn('Error cargando registros faltantes:', err?.message || err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tolerancia, lookback]);

  useEffect(() => { cargar(); }, [cargar, refreshKey]);

  const total = data?.total_pendientes || 0;
  const porFecha = data?.por_fecha || [];
  const porNave = data?.por_nave || [];

  // Si no hay alertas, mostramos un mensaje verde compacto y permitimos
  // ajustar la configuración para volver a buscar con otros parámetros.
  const isOk = !loading && data && total === 0;

  const fechasMostradas = useMemo(() => (
    expanded ? porFecha : porFecha.slice(0, 3)
  ), [expanded, porFecha]);

  if (!data && !loading) return null;

  return (
    <Card
      className={
        isOk
          ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-950/30'
          : 'border-amber-300 bg-amber-50/70 dark:border-amber-900/50 dark:bg-amber-950/30'
      }
      data-testid="card-registros-faltantes"
    >
      <CardContent className="py-3">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            {isOk ? (
              <Calendar className="w-5 h-5 text-emerald-700 dark:text-emerald-300 mt-0.5 shrink-0" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <h3
                className={`text-sm font-semibold ${isOk ? 'text-emerald-900 dark:text-emerald-100' : 'text-amber-900 dark:text-amber-100'}`}
                style={{ fontFamily: 'Space Grotesk' }}
                data-testid="faltantes-titulo"
              >
                {loading
                  ? 'Verificando registros pendientes…'
                  : isOk
                    ? '✓ Todas las naves al día'
                    : `${total} registros pendientes (${porFecha.length} fechas con atraso)`}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isOk
                  ? `Ninguna nave con atraso mayor a ${tolerancia} día${tolerancia === 1 ? '' : 's'} en la ventana revisada.`
                  : `Faltan registros de hace más de ${tolerancia} día${tolerancia === 1 ? '' : 's'}. Click en una fila para pre-cargar el formulario.`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => setShowSettings((v) => !v)}
              title="Configurar tolerancia / ventana"
              data-testid="btn-settings-faltantes"
            >
              <Settings2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={cargar}
              disabled={loading}
              title="Recargar"
              data-testid="btn-refresh-faltantes"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {!isOk && total > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setExpanded((v) => !v)}
                data-testid="btn-expand-faltantes"
              >
                {expanded ? <><ChevronUp className="w-4 h-4 mr-1" /> Ocultar</> : <><ChevronDown className="w-4 h-4 mr-1" /> Ver detalle</>}
              </Button>
            )}
          </div>
        </div>

        {/* Settings collapsible */}
        {showSettings && (
          <div className="mt-3 pt-3 border-t border-amber-200/50 dark:border-amber-900/40 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Tolerancia</label>
              <Select value={String(tolerancia)} onValueChange={(v) => setTolerancia(Number(v))}>
                <SelectTrigger className="h-9 mt-1" data-testid="select-tolerancia">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TOLERANCIA_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Ventana hacia atrás</label>
              <Select value={String(lookback)} onValueChange={(v) => setLookback(Number(v))}>
                <SelectTrigger className="h-9 mt-1" data-testid="select-lookback">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOOKBACK_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[11px] text-muted-foreground sm:col-span-2 -mt-1">
              Rango por nave: desde su <strong>primer registro de producción</strong> (o el
              override configurado en <em>Configuración → Naves</em>) hasta hoy. La
              ventana solo aplica como filtro adicional para limitar la vista.
            </p>
          </div>
        )}

        {/* Resumen rápido por nave (siempre visible si hay pendientes) */}
        {!isOk && total > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {porNave.map((n) => (
              <Badge
                key={n.nave_id}
                variant={atrasoVariant(n.max_dias_atraso)}
                className="text-xs tabular-nums"
                title={`Máx ${n.max_dias_atraso} días de atraso`}
              >
                {n.nave_nombre}: {n.fechas_faltantes}
              </Badge>
            ))}
          </div>
        )}

        {/* Detalle por fecha (expandible) */}
        {!isOk && total > 0 && (
          <div className="mt-3 space-y-2">
            {fechasMostradas.map((f) => (
              <div
                key={f.fecha}
                className="rounded-md border border-amber-200/60 dark:border-amber-900/40 bg-white/60 dark:bg-amber-950/10 p-2.5"
                data-testid={`faltante-fecha-${f.fecha}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2 text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                    <Calendar className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                    <span>{formatDate(f.fecha)}</span>
                    <Badge variant={atrasoVariant(f.dias_atraso)} className="text-[10px]">
                      {f.dias_atraso} día{f.dias_atraso === 1 ? '' : 's'} de atraso
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {f.naves.length} nave{f.naves.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {f.naves.map((n) => (
                    <Button
                      key={`${f.fecha}-${n.nave_id}`}
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => onSelectFaltante?.({
                        nave_id: n.nave_id,
                        nave_nombre: n.nave_nombre,
                        fecha: f.fecha,
                      })}
                      data-testid={`btn-faltante-${f.fecha}-${n.nave_id}`}
                      title="Click para pre-cargar el formulario con esta nave y fecha"
                    >
                      {n.nave_nombre}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
            {!expanded && porFecha.length > fechasMostradas.length && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() => setExpanded(true)}
              >
                Ver {porFecha.length - fechasMostradas.length} fecha{porFecha.length - fechasMostradas.length === 1 ? '' : 's'} más
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
